import * as MD from '@material-ui/core';
import { SvgIcon } from '@material-ui/core';
import MuiTextField from '@material-ui/core/TextField';
import * as React from 'react';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { Dispatch } from 'redux';
import styled from 'styled-components';
import { NotificationBar } from '../Components/NotificationBarComponent';
import { IApplicationState, IConnectedReduxProps } from '../Store';
import { I18N } from '../Store/I18n/Types';
import { ILayoutState } from '../Store/Layout/Types';
import { INotificationBarState } from '../Store/NotificationBar/Types';
import { login } from '../Store/User/Actions';
import { IUserState } from '../Store/User/Types';
import { getTheme } from '../Themes';
import { YacastLogoIcon } from '../Themes/svg/yacast-logo';

interface IPropsFromState {
	i18n: I18N;
	layout: ILayoutState;
	notificationBar: INotificationBarState;
	user: IUserState;
	router: RouteComponentProps<{}>;
}

interface IState {
	username: string;
	password: string;
}

interface IPropsFromDispatch {
	login: typeof login;
}

type AllProps = MD.WithStyles<typeof styles> &
	IPropsFromState &
	IPropsFromDispatch &
	RouteComponentProps<{}> &
	IConnectedReduxProps;

const theme = getTheme();

const styles = MD.createStyles({
	button: {
		margin: theme.spacing(1),
		marginLeft: 0,
		marginRight: 0,
		textTransform: 'uppercase',
	},
	card: {
		maxWidth: '40%',
		minWidth: '20%',
		marginTop: '50px',
	},
	cardContent: {
		width: '100%',
	},
	errorContainer: {
		width: '100%',
		justifyContent: 'center',
		textAlign: 'center',
		color: 'red',
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		flexWrap: 'wrap',
	},
	media: {
		height: 140,
	},
});

const DensedTextField = styled(MuiTextField)`
	.MuiInputBase-input {
		padding: 4px 6px;
		font-size: 13px;
	}
	.MuiInputLabel-formControl {
		top: -7px;
		font-size: 13px;
		font-weight: 300;
	}
`;

export class LoginPage extends React.Component<AllProps, IState> {
	public constructor(props: AllProps) {
		super(props);
		this.state = {
			password: '',
			username: '',
		};
	}

	public render(): React.ReactNode {
		if (this.props.user.username !== '') {
			return <Redirect to="/" />;
		}

		const { classes, layout } = this.props;
		const theme = getTheme(layout.theme);
		let errorMessage = null;
		if (this.props.notificationBar.autherror) {
			errorMessage = this.props.notificationBar.autherror
				.message as string;
		}

		if (errorMessage) {
			errorMessage = this.props.i18n._(errorMessage);
		}

		return (
			<MD.Card className={classes.card}>
				<MD.CardActionArea></MD.CardActionArea>
				<MD.CardActions>
					<MD.CardContent className={classes.cardContent}>
						<MD.Typography
							gutterBottom={true}
							variant="h4"
							component="h2"
						>
							<SvgIcon
								component={YacastLogoIcon}
								htmlColor={theme.palette.text.primary}
							/>
						</MD.Typography>

						<MD.Typography
							variant="caption"
							color="textSecondary"
							component="p"
						></MD.Typography>
						<form
							className={classes.form}
							noValidate={true}
							onSubmit={this.onSubmit.bind(this)}
						>
							<DensedTextField
								autoComplete="username"
								label={this.props.i18n._('Username')}
								onChange={this.onUsernameChange.bind(this)}
								value={this.state.username}
							/>
							<DensedTextField
								autoComplete="off"
								label={this.props.i18n._('Password')}
								margin="normal"
								onChange={this.onPasswordChange.bind(this)}
								onKeyPress={(e) => {
									if (e.key === 'Enter') {
										this.onSubmit();
									}
								}}
								type="password"
								value={this.state.password}
							/>
							<MD.Button
								className={classes.button}
								color="primary"
								variant="contained"
								onClick={this.onSubmit.bind(this)}
							>
								{this.props.i18n._('Log in')}
							</MD.Button>
							<MD.Link href="/reset" variant="body2">
								Forgot password?
							</MD.Link>
							<MD.Grid className={classes.errorContainer}>
								{errorMessage}
							</MD.Grid>
						</form>
					</MD.CardContent>
				</MD.CardActions>

				<NotificationBar />
			</MD.Card>
		);
	}

	private onUsernameChange(ev: React.ChangeEvent<HTMLInputElement>) {
		this.setState({ username: ev.target.value });
	}

	private onPasswordChange(ev: React.ChangeEvent<HTMLInputElement>) {
		this.setState({ password: ev.target.value });
	}

	private onSubmit() {
		this.props.login(this.state.username, this.state.password);
	}
}

const mapStateToProps = (
	{ i18n, layout, notificationBar, user }: IApplicationState,
	ownProps: RouteComponentProps<{}>
) => ({
	i18n: i18n.i18n,
	layout,
	notificationBar,
	user,
	router: ownProps,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	login: (u: string, p: string) => dispatch(login(u, p)),
});

export const Login = connect(
	mapStateToProps,
	mapDispatchToProps
)(MD.withStyles(styles)(LoginPage));
